import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Button, Badge, Modal, Form, Table } from 'react-bootstrap';
import { Plus, X, Copy, Edit2 } from 'lucide-react';
import { useAdminMenu } from '../../context/AdminMenuContext';
import { useBranch } from '../../context/BranchContext';
import { branchAPI } from '../../services';
import { toast } from 'react-toastify';

// Copy Menu Modal
const CopyMenuModal = ({ show, onHide, branches, onCopy }) => {
  const [formData, setFormData] = useState({
    fromBranchId: '',
    toBranchId: '',
    adjustmentType: 'exact',
    adjustmentValue: 0,
    adjustmentDirection: 'increase', // 'increase' hoặc 'decrease'
  });

  useEffect(() => {
    if (show) {
      setFormData({
        fromBranchId: branches[0]?.id || '',
        toBranchId: branches[1]?.id || '',
        adjustmentType: 'exact',
        adjustmentValue: 0,
        adjustmentDirection: 'increase',
      });
    }
  }, [show, branches]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.fromBranchId === formData.toBranchId) {
      toast.error('Không thể copy menu từ chi nhánh sang chính nó!');
      return;
    }
    onCopy(formData);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Sao chép menu giữa các chi nhánh</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Chi nhánh nguồn (copy từ) *</Form.Label>
            <Form.Select
              value={formData.fromBranchId}
              onChange={(e) => setFormData({ ...formData, fromBranchId: e.target.value })}
              required
            >
              <option value="">Chọn chi nhánh nguồn</option>
              {branches.map((branch) => (
                <option
                  key={branch.id}
                  value={branch.id}
                >
                  {branch.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Chi nhánh đích (copy đến) *</Form.Label>
            <Form.Select
              value={formData.toBranchId}
              onChange={(e) => setFormData({ ...formData, toBranchId: e.target.value })}
              required
            >
              <option value="">Chọn chi nhánh đích</option>
              {branches
                .filter((b) => b.id !== formData.fromBranchId)
                .map((branch) => (
                  <option
                    key={branch.id}
                    value={branch.id}
                  >
                    {branch.name}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Loại điều chỉnh giá</Form.Label>
            <Form.Select
              value={formData.adjustmentType}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  adjustmentType: e.target.value,
                  adjustmentValue: 0,
                  adjustmentDirection: 'increase',
                })
              }
            >
              <option value="exact">Sao chép y chang giá</option>
              <option value="percentage">Điều chỉnh theo phần trăm (%)</option>
              <option value="amount">Điều chỉnh theo số tiền (VNĐ)</option>
            </Form.Select>
          </Form.Group>

          {formData.adjustmentType !== 'exact' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Hướng điều chỉnh</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    id="adjust-increase"
                    name="adjustmentDirection"
                    label="Tăng giá"
                    checked={formData.adjustmentDirection === 'increase'}
                    onChange={() => setFormData({ ...formData, adjustmentDirection: 'increase' })}
                  />
                  <Form.Check
                    type="radio"
                    id="adjust-decrease"
                    name="adjustmentDirection"
                    label="Giảm giá"
                    checked={formData.adjustmentDirection === 'decrease'}
                    onChange={() => setFormData({ ...formData, adjustmentDirection: 'decrease' })}
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  {formData.adjustmentType === 'percentage'
                    ? `Số phần trăm ${formData.adjustmentDirection === 'increase' ? 'tăng' : 'giảm'}`
                    : `Số tiền ${formData.adjustmentDirection === 'increase' ? 'tăng' : 'giảm'} (VNĐ)`}
                </Form.Label>
                <Form.Control
                  type="number"
                  value={formData.adjustmentValue}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : Number(e.target.value);
                    setFormData({ ...formData, adjustmentValue: Math.abs(value) });
                  }}
                  min="0"
                  step={formData.adjustmentType === 'percentage' ? 1 : 1000}
                  placeholder={formData.adjustmentType === 'percentage' ? '10' : '5000'}
                />
                <Form.Text className="text-muted">
                  {formData.adjustmentType === 'percentage'
                    ? `Nhập số phần trăm muốn ${
                        formData.adjustmentDirection === 'increase' ? 'tăng' : 'giảm'
                      } (ví dụ: 10 = ${formData.adjustmentDirection === 'increase' ? '+' : '-'}10%)`
                    : `Nhập số tiền muốn ${
                        formData.adjustmentDirection === 'increase' ? 'tăng' : 'giảm'
                      } (ví dụ: 5000 = ${formData.adjustmentDirection === 'increase' ? '+' : '-'}5000đ)`}
                </Form.Text>
              </Form.Group>
            </>
          )}

          <div className="alert alert-info">
            <strong>Lưu ý:</strong> Menu hiện tại của chi nhánh đích sẽ bị thay thế hoàn toàn bằng menu từ chi nhánh
            nguồn.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            type="submit"
          >
            <Copy
              size={16}
              className="me-1"
            />
            Sao chép menu
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// Price Edit Modal
const PriceEditModal = ({ show, onHide, menuItem, currentPrice, basePrice, onSave }) => {
  const [price, setPrice] = useState(currentPrice !== null && currentPrice !== undefined ? currentPrice : '');

  useEffect(() => {
    setPrice(currentPrice !== null && currentPrice !== undefined ? currentPrice : '');
  }, [currentPrice, show]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Nếu giá rỗng hoặc 0, gửi null để dùng giá mặc định
    const finalPrice = price === '' || price === 0 ? null : Number(price);
    onSave(finalPrice);
  };

  if (!menuItem) return null;

  const defaultPrice = basePrice || menuItem.price || 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Chỉnh sửa giá - {menuItem.name}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Giá mặc định: {defaultPrice.toLocaleString('vi-VN')}đ</Form.Label>
            <Form.Control
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value === '' ? '' : Number(e.target.value))}
              min="0"
              step="1000"
              placeholder="Để trống để dùng giá mặc định"
            />
            <Form.Text className="text-muted">
              Để trống để dùng giá mặc định ({defaultPrice.toLocaleString('vi-VN')}đ). Nhập giá khác để set giá riêng
              cho chi nhánh này.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            type="submit"
          >
            Lưu giá
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const AdminBranchMenus = () => {
  const { branches, menuItems, fetchBranches, fetchMenuItems } = useAdminMenu();
  // #175019 — chi nhánh đang xem/sửa menu lấy từ thanh trên đầu, bỏ ô "Chọn chi nhánh *" của màn.
  // Modal "Sao chép menu" vẫn giữ 2 ô nguồn/đích vì đó là thao tác GIỮA 2 chi nhánh — thanh
  // trên đầu chỉ nói được 1 chi nhánh.
  const { selectedBranch: headerBranch } = useBranch();
  const selectedBranch = useMemo(
    () => (headerBranch?.id ? branches.find((b) => b.id === headerBranch.id) || headerBranch : null),
    [headerBranch, branches],
  );
  const [branchMenuItems, setBranchMenuItems] = useState([]);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBranches();
    fetchMenuItems();
  }, [fetchBranches, fetchMenuItems]);

  const loadBranchMenu = useCallback(async (branchId) => {
    if (!branchId) {
      setBranchMenuItems([]);
      return;
    }
    try {
      setLoading(true);
      console.log('🔄 Fetching menu for branch:', branchId);
      const response = await branchAPI.getBranchMenu(branchId);
      console.log('📦 Branch menu response:', response);

      // Response format:
      // {
      //   branch: { id, name, code },
      //   menuItems: [{ menu_item_id, name, base_price, price_override, final_price, ... }]
      // }
      const menuData = response.data?.menuItems || response.menuItems || [];
      console.log('✅ Parsed menu items:', menuData);
      setBranchMenuItems(menuData);
    } catch (error) {
      console.error('❌ Error loading branch menu:', error);
      toast.error('Lỗi khi tải menu chi nhánh');
      setBranchMenuItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const branchId = selectedBranch?.id;
    if (branchId) {
      loadBranchMenu(branchId);
    } else {
      setBranchMenuItems([]);
    }
  }, [selectedBranch?.id, loadBranchMenu]);

  const handleAddMenuItem = async (menuItemId) => {
    if (!selectedBranch) return;
    try {
      await branchAPI.addMenuItemToBranch(selectedBranch.id, menuItemId);
      toast.success('Thêm món vào menu thành công!');
      loadBranchMenu(selectedBranch.id);
    } catch (error) {
      console.error('Error adding menu item:', error);
      toast.error('Lỗi khi thêm món vào menu');
    }
  };

  const handleRemoveMenuItem = async (menuItemId) => {
    if (!selectedBranch) return;
    if (!window.confirm('Bạn có chắc muốn xóa món này khỏi menu?')) return;

    try {
      await branchAPI.removeMenuItemFromBranch(selectedBranch.id, menuItemId);
      toast.success('Xóa món khỏi menu thành công!');
      loadBranchMenu(selectedBranch.id);
    } catch (error) {
      console.error('Error removing menu item:', error);
      toast.error('Lỗi khi xóa món khỏi menu');
    }
  };

  const handleClearMenu = async () => {
    if (!selectedBranch) return;
    const confirmMessage = `Bạn có chắc muốn xóa TẤT CẢ ${branchMenuItems.length} món khỏi menu của "${selectedBranch.name}"?\n\nHành động này không thể hoàn tác!`;
    if (!window.confirm(confirmMessage)) return;

    try {
      await branchAPI.clearBranchMenu(selectedBranch.id);
      toast.success('Đã xóa tất cả món khỏi menu!');
      loadBranchMenu(selectedBranch.id);
    } catch (error) {
      console.error('Error clearing branch menu:', error);
      toast.error('Lỗi khi xóa menu');
    }
  };

  const handleCopyMenu = async (formData) => {
    try {
      // Convert adjustmentValue: nếu là decrease thì chuyển sang số âm
      const finalAdjustmentValue =
        formData.adjustmentType === 'exact'
          ? 0
          : formData.adjustmentDirection === 'decrease'
          ? -Math.abs(formData.adjustmentValue)
          : Math.abs(formData.adjustmentValue);

      await branchAPI.copyBranchMenu(
        formData.fromBranchId,
        formData.toBranchId,
        formData.adjustmentType,
        finalAdjustmentValue,
      );
      toast.success('Sao chép menu thành công!');
      setShowCopyModal(false);
      if (selectedBranch?.id === formData.toBranchId) {
        loadBranchMenu(selectedBranch.id);
      }
    } catch (error) {
      console.error('Error copying menu:', error);
      toast.error('Lỗi khi sao chép menu');
    }
  };

  const handleEditPrice = (menuItem) => {
    const branchMenuItem = branchMenuItems.find((item) => (item.menu_item_id || item.id) === menuItem.id);
    setEditingPrice({
      menuItem: {
        ...menuItem,
        name: branchMenuItem?.name || menuItem.name,
      },
      currentPrice: branchMenuItem?.price_override ?? null,
      basePrice: branchMenuItem?.base_price || menuItem.price || 0,
    });
    setShowPriceModal(true);
  };

  const handleSavePrice = async (price) => {
    if (!selectedBranch || !editingPrice) return;
    try {
      const response = await branchAPI.updateMenuItemPrice(selectedBranch.id, editingPrice.menuItem.id, price || null);
      const message = response?.message || response?.data?.message || 'Cập nhật giá thành công!';
      toast.success(message);
      setShowPriceModal(false);
      setEditingPrice(null);
      loadBranchMenu(selectedBranch.id);
    } catch (error) {
      console.error('Error updating price:', error);
      toast.error('Lỗi khi cập nhật giá');
    }
  };

  // Removed getMenuItemPrice - using final_price directly from API response

  const availableMenuItems = menuItems.filter(
    (item) => !branchMenuItems.some((bItem) => (bItem.menu_item_id || bItem.id) === item.id),
  );

  return (
    <>
      <Card className="shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">Quản lý Menu Chi nhánh</h4>
              <small className="text-muted">Thêm/xóa món và set giá riêng cho từng chi nhánh</small>
            </div>
            <div className="d-flex gap-2">
              {selectedBranch && branchMenuItems.length > 0 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleClearMenu}
                >
                  <X
                    size={18}
                    className="me-1"
                  />
                  Xóa tất cả menu
                </Button>
              )}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowCopyModal(true)}
              >
                <Copy
                  size={18}
                  className="me-1"
                />
                Sao chép menu
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {selectedBranch && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Menu của {selectedBranch.name}</h5>
                <Badge bg="info">{branchMenuItems.length} món</Badge>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div
                    className="spinner-border text-primary"
                    role="status"
                  >
                    <span className="visually-hidden">Đang tải...</span>
                  </div>
                </div>
              ) : (
                <>
                  <Table
                    striped
                    bordered
                    hover
                    responsive
                  >
                    <thead>
                      <tr>
                        <th>Món</th>
                        <th>Giá mặc định</th>
                        <th>Giá chi nhánh</th>
                        <th>Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branchMenuItems.map((branchItem) => {
                        // branchItem từ API có structure:
                        // { menu_item_id, name, base_price, price_override, final_price, ... }
                        const menuItemId = branchItem.menu_item_id || branchItem.id;
                        const menuItem = menuItems.find((item) => item.id === menuItemId) || branchItem;

                        const basePrice = branchItem.base_price || branchItem.price || 0;
                        const priceOverride = branchItem.price_override;
                        const finalPrice = branchItem.final_price || priceOverride || basePrice;
                        const hasCustomPrice = priceOverride !== null && priceOverride !== undefined;

                        return (
                          <tr key={menuItemId}>
                            <td>
                              <strong>{branchItem.name || menuItem.name}</strong>
                              {hasCustomPrice && (
                                <Badge
                                  bg="warning"
                                  text="dark"
                                  className="ms-2"
                                >
                                  Giá riêng
                                </Badge>
                              )}
                            </td>
                            <td>{basePrice.toLocaleString('vi-VN')}đ</td>
                            <td>
                              <strong className={hasCustomPrice ? 'text-primary' : ''}>
                                {finalPrice.toLocaleString('vi-VN')}đ
                              </strong>
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEditPrice({ ...menuItem, id: menuItemId, price: basePrice })}
                              >
                                <Edit2
                                  size={14}
                                  className="me-1"
                                />
                                Sửa giá
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveMenuItem(menuItemId)}
                              >
                                <X size={14} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>

                  {branchMenuItems.length === 0 && (
                    <div className="text-center py-4 text-muted">
                      Chưa có món nào trong menu. Hãy thêm món từ danh sách bên dưới.
                    </div>
                  )}

                  <hr className="my-4" />

                  <h6 className="mb-3">Thêm món vào menu</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {availableMenuItems.length > 0 ? (
                      availableMenuItems.map((item) => (
                        <Button
                          key={item.id}
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleAddMenuItem(item.id)}
                        >
                          <Plus
                            size={14}
                            className="me-1"
                          />
                          {item.name}
                        </Button>
                      ))
                    ) : (
                      <p className="text-muted">Tất cả món đã có trong menu</p>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {!selectedBranch && (
            <div className="text-center py-5 text-muted">
              Chọn chi nhánh ở thanh trên đầu trang để xem và sửa menu của chi nhánh đó.
            </div>
          )}
        </Card.Body>
      </Card>

      <CopyMenuModal
        show={showCopyModal}
        onHide={() => setShowCopyModal(false)}
        branches={branches}
        onCopy={handleCopyMenu}
      />

      {editingPrice && (
        <PriceEditModal
          show={showPriceModal}
          onHide={() => {
            setShowPriceModal(false);
            setEditingPrice(null);
          }}
          menuItem={editingPrice.menuItem}
          currentPrice={editingPrice.currentPrice}
          basePrice={editingPrice.basePrice}
          onSave={handleSavePrice}
        />
      )}
    </>
  );
};

export default AdminBranchMenus;
